import { callApi, RequestType } from "../fetch";
import { BookingInterval } from "./ev_bookings";
import { UserModel } from "./user_repository";

// Free spaces at a person's company, as the booking app shows them: for each parking type, and per day.
export type Pool = { available: number; offered: boolean };
export type DaySpaces = BookingInterval & { standard: Pool; ev: Pool };
// The tightest day for each type, and (when asked) every day.
export type Spaces = { standard: Pool; ev: Pool; days: DaySpaces[] };

type Pools = { standard?: Pool; ev?: Pool; intervalAvailability?: DaySpaces[] };
type Answer = { data?: Pools };

// Whose free spaces to ask about: the person, and their company's record ID for a company that hasn't switched
// over (whose people may have no capacityTenantId yet).
export type SpacesAsk = Pick<UserModel, "userId" | "capacityTenantId"> & { companyId?: string };

// A booking being changed: the service leaves it (or its whole series) out of the count, as the app's edit
// calendar does, so its own space doesn't show as taken. It must be the person's own booking.
export type Editing = { bookingId: string; wholeSeries: boolean };

export const canAskSpaces = (person: SpacesAsk | undefined) => Boolean(person?.userId && (person.capacityTenantId || person.companyId));

const spacesFrom = (pools: Pools | null | undefined): Spaces | null =>
    pools?.standard && pools?.ev ? { standard: pools.standard, ev: pools.ev, days: pools.intervalAvailability ?? [] } : null;

// Free spaces, as the app asks: a switched-over company through the booking service (for the person, so an
// admin's rights to act for them apply); any other company through the older calendar route, which answers
// anyone at that company, so an admin at another company (a super admin) gets no answer.
// Resolves null when there's no answer for this person; throws when the service can't be reached, so the
// question is asked again.
export const spacesFor = async (person: SpacesAsk, intervals: BookingInterval[],
    { perDay = false, editing }: { perDay?: boolean; editing?: Editing } = {}): Promise<Spaces | null> => {
    if (!person.userId || intervals.length === 0) return null;
    const days = perDay ? { includeIntervals: true } : {};
    if (person.capacityTenantId) {
        // The app's calendar route, which reads every booking's times as Sydney wall-clock, as they're stored.
        const response = await callApi<Answer>("bookingApiV2/availability", { tenantId: person.capacityTenantId, userId: person.userId,
            intervals, timeEncoding: "sydney-wall-v1", ...days,
            ...(editing ? { bookingId: editing.bookingId, ...(editing.wholeSeries ? { excludeSeries: true } : {}) } : {}) }, RequestType.POST, { signedIn: true });
        if (!response.error) return spacesFrom(response.data?.data);
        if (!response.status || response.status >= 500) throw new Error("The booking service didn't answer.");
        // 423: the company hasn't switched over, so the older route counts for it.
        if (response.status !== 423) return null;
    }
    // The older route only leaves a booking out of the count for its own owner, so a change an admin makes for
    // someone isn't counted there.
    const companyId = person.companyId ?? person.capacityTenantId;
    if (editing || !companyId) return null;
    const response = await callApi<Pools>("getBookingAvailability", { tenantId: companyId, intervals, ...days }, RequestType.POST, { signedIn: true });
    if (response.error) {
        if (!response.status || response.status >= 500) throw new Error("The booking service didn't answer.");
        return null;
    }
    return spacesFrom(response.data);
};

// The service answers up to 42 days at once, so a calendar month is one question.
export const monthDays = (month: Date, toIntervals: (from: Date, to: Date) => BookingInterval[]): BookingInterval[] => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const first = new Date(month.getFullYear(), month.getMonth(), 1);
    const last = new Date(month.getFullYear(), month.getMonth() + 1, 0);
    const from = first < today ? today : first;
    return from > last ? [] : toIntervals(from, last).filter((day) => day.endTime > day.startTime);
};
